// 路由配置文件

import { lazy } from 'react';
import type { RouteHandle } from '@/types';

// 懒加载页面组件
export const HomePage = lazy(() => import('@/pages/HomePage'));
export const DiscoverPage = lazy(() => import('@/pages/DiscoverPage'));
export const FavoritesPage = lazy(() => import('@/pages/FavoritesPage'));
export const AboutPage = lazy(() => import('@/pages/AboutPage'));
export const CorsTestPage = lazy(() => import('@/pages/CorsTestPage'));

// 阅读相关页面组件
export const BookLibraryPage = lazy(() => import('@/pages/reading/BookLibraryPage'));
export const BookSourcesPage = lazy(() => import('@/pages/reading/BookSourcesPage'));
export const BookReaderPage = lazy(() => import('@/pages/reading/BookReaderPage'));

// 路由元数据配置
export const routeHandles: Record<string, RouteHandle> = {
  '/': {
    title: '首页',
    description: 'Dopamine Web App 主页 - 现代化的React+Redux应用',
    preload: () => import('@/pages/HomePage')
  },
  '/discover': {
    title: '发现',
    description: '发现新内容和功能 - 探索更多可能性',
    preload: () => import('@/pages/DiscoverPage')
  },
  '/favorites': {
    title: '收藏',
    description: '我的收藏内容 - 保存喜欢的内容',
    preload: () => import('@/pages/FavoritesPage')
  },
  '/about': {
    title: '关于',
    description: '关于 Dopamine Web App - 了解我们的产品',
    preload: () => import('@/pages/AboutPage')
  },
  '/cors-test': {
    title: 'CORS 测试',
    description: '跨域请求功能测试 - 验证网络请求能力',
    preload: () => import('@/pages/CorsTestPage')
  },
  '/library': {
    title: '书库',
    description: '我的图书库 - 浏览和管理图书',
    preload: () => import('@/pages/reading/BookLibraryPage')
  },
  '/sources': {
    title: '书源管理',
    description: '管理图书源 - 添加和配置书源',
    preload: () => import('@/pages/reading/BookSourcesPage')
  },
  '/reader': {
    title: '阅读器',
    description: '图书阅读界面 - 沉浸式阅读体验',
    preload: () => import('@/pages/reading/BookReaderPage')
  }
};

// 路由路径常量
export const ROUTES = {
  HOME: '/',
  DISCOVER: '/discover',
  FAVORITES: '/favorites',
  ABOUT: '/about',
  CORS_TEST: '/cors-test',
  LIBRARY: '/library',
  SOURCES: '/sources',
  READER: '/reader'
} as const;

// 导航菜单配置
export const navigationItems = [
  {
    path: ROUTES.HOME,
    label: '首页',
    icon: 'Home',
    description: '返回主页'
  },
  {
    path: ROUTES.LIBRARY,
    label: '书库',
    icon: 'Book',
    description: '我的图书库'
  },
  {
    path: ROUTES.DISCOVER,
    label: '发现',
    icon: 'Search',
    description: '发现新内容'
  },
  {
    path: ROUTES.FAVORITES,
    label: '收藏',
    icon: 'Heart',
    description: '我的收藏'
  },
  {
    path: ROUTES.SOURCES,
    label: '书源管理',
    icon: 'Settings',
    description: '管理书源'
  },
  {
    path: ROUTES.ABOUT,
    label: '关于',
    icon: 'Info',
    description: '关于应用'
  }
] as const;

// 底部标签栏配置
export const tabBarItems = [
  {
    path: ROUTES.HOME,
    label: '首页',
    icon: 'Home',
    activeIcon: 'HomeFilled'
  },
  {
    path: ROUTES.LIBRARY,
    label: '书库',
    icon: 'Book',
    activeIcon: 'BookFilled'
  },
  {
    path: ROUTES.DISCOVER,
    label: '发现',
    icon: 'Search',
    activeIcon: 'SearchFilled'
  },
  {
    path: ROUTES.FAVORITES,
    label: '收藏',
    icon: 'Heart',
    activeIcon: 'HeartFilled'
  }
] as const;

// 路由权限配置
export const routePermissions: Record<string, { requiresAuth?: boolean; roles?: string[] }> = {
  '/': { requiresAuth: false },
  '/discover': { requiresAuth: false },
  '/favorites': { requiresAuth: false },
  '/about': { requiresAuth: false },
  '/cors-test': { requiresAuth: false },
  '/library': { requiresAuth: false },
  '/sources': { requiresAuth: false },
  '/reader': { requiresAuth: false }
};

// 获取路由元数据
export const getRouteHandle = (path: string): RouteHandle | undefined => {
  return routeHandles[path];
};

// 检查路由权限
export const checkRoutePermission = (
  path: string, 
  isAuthenticated: boolean = false, 
  userRoles: string[] = []
): boolean => {
  const permission = routePermissions[path];
  
  if (!permission) {
    return true; // 默认允许访问
  }
  
  if (permission.requiresAuth && !isAuthenticated) {
    return false;
  }
  
  if (permission.roles && permission.roles.length > 0) {
    return permission.roles.some(role => userRoles.includes(role));
  }
  
  return true;
};

// 预加载路由
export const preloadRoute = async (path: string): Promise<void> => {
  const handle = getRouteHandle(path);
  if (handle?.preload) {
    try {
      await handle.preload();
    } catch (error) {
      console.warn(`Failed to preload route ${path}:`, error);
    }
  }
};

// 预加载所有路由
export const preloadAllRoutes = async (): Promise<void> => {
  const preloadPromises = Object.values(routeHandles)
    .filter(handle => handle.preload)
    .map(handle => handle.preload!());
    
  try {
    await Promise.all(preloadPromises);
  } catch (error) {
    console.warn('Failed to preload some routes:', error);
  }
};

export type RouteKey = keyof typeof ROUTES;
export type RoutePath = typeof ROUTES[RouteKey];